import DemoAsset from '../components/DemoAsset';
import PerceptionStack from '../components/PerceptionStack';
import ReconstructionViz from '../components/ReconstructionViz';

export default function Technology() {
  const pipeline = [
    ['01', 'Sensing', 'LiDAR, vision, IMU and depth observe geometry, appearance and motion.'],
    ['02', 'Sync + calibration', 'Time-aligned, calibrated streams become one coherent observation.'],
    ['03', 'Localization', 'The system estimates its position and orientation within the environment.'],
    ['04', 'Reconstruction', 'Observations become persistent point clouds, geometry and spatial models.'],
    ['05', 'Semantics', 'Objects, surfaces and regions gain identity and operational meaning.'],
    ['06', 'Spatial reasoning', 'Applications query, compare and reason about places over time.'],
    ['07', 'Edge + cloud', 'Time-critical perception runs locally; heavier reconstruction scales in the cloud.'],
  ];

  return (
    <>
      <section className="bv-tech-hero">
        <div className="bv-wrap">
          <div className="bv-tech-hero-copy">
            <p className="bv-eyebrow">Technology / Perception core</p>
            <h1 className="bv-h1">A spatial model of<br />the real world.</h1>
            <p className="bv-lead">Black Vanilla fuses multimodal sensing into persistent representations of geometry, motion and meaning — built for products operating in physical space.</p>
          </div>
          <div className="bv-tech-scan">
            <ReconstructionViz />
            <span>PERCEPTION_CORE / LIVE</span>
          </div>
        </div>
      </section>

      <PerceptionStack />

      <section className="bv-pipeline-section" aria-labelledby="pipeline-title">
        <div className="bv-wrap">
          <header className="bv-section-head bv-section-head-wide">
            <div><p className="bv-eyebrow">System pipeline / 01—07</p><h2 id="pipeline-title" className="bv-h2">From signal<br />to spatial reasoning.</h2></div>
            <p className="bv-lead">Each layer turns raw physical observation into a model an application can use.</p>
          </header>
          <ol className="bv-pipeline">
            {pipeline.map(([number, title, body]) => (
              <li key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{body}</p>
                <i aria-hidden="true" />
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bv-modalities-section" aria-labelledby="modalities-title">
        <div className="bv-wrap">
          <p className="bv-eyebrow">Multimodal input</p>
          <h2 id="modalities-title" className="bv-h2">No single sensor sees the whole world.</h2>
          <div className="bv-modality-band">
            {[
              ['LIDAR', 'Range + structure', '360°'],
              ['RGB', 'Appearance + context', 'VIS'],
              ['IMU', 'Motion + orientation', '6-AXIS'],
              ['STEREO', 'Near-field depth', 'DEPTH'],
            ].map(([name, detail, code]) => (
              <article key={name}>
                <div className={`bv-sensor-glyph glyph-${name.toLowerCase()}`} aria-hidden="true"><i /><i /></div>
                <span>{code}</span><h3>{name}</h3><p>{detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bv-architecture-section" aria-labelledby="arch-title">
        <div className="bv-wrap">
          <header className="bv-section-head bv-section-head-wide">
            <div><p className="bv-eyebrow">Execution architecture</p><h2 id="arch-title" className="bv-h2">Fast at the edge.<br />Deep in the cloud.</h2></div>
            <p className="bv-lead">Execution is placed where the physical loop needs it: low-latency awareness on-device, scalable reconstruction and collaboration off-device.</p>
          </header>
          <DemoAsset
            id="architecture-graphic"
            fallback={<ExecutionGraphic />}
          />
        </div>
      </section>

      <section className="bv-hardware-section">
        <div className="bv-wrap bv-split">
          <div>
            <p className="bv-eyebrow">Physical systems / Prototype</p>
            <h2 className="bv-h2">The stack starts<br />in the field.</h2>
            <p className="bv-lead">
              We are exploring purpose-built capture hardware for environments where a phone is not the right sensor. This work remains at prototype stage.
            </p>
            <div className="bv-tech-spec-line"><span>Sensor synchronization</span><span>Edge compute</span><span>Field capture</span></div>
          </div>
          <DemoAsset
            id="perception-hardware"
            fallback={<HardwarePlaceholder />}
          />
        </div>
      </section>
    </>
  );
}

function ExecutionGraphic() {
  return (
    <div className="bv-execution-graphic" aria-hidden="true">
      <div className="bv-execution-zone"><span>EDGE / DEVICE</span><strong>Sensor fusion<br />Localization<br />Live mapping</strong><small>LOW LATENCY LOOP</small></div>
      <div className="bv-execution-link"><i /><b>ENCRYPTED SPATIAL STREAM</b><i /></div>
      <div className="bv-execution-zone"><span>CLOUD / SCALE</span><strong>Reconstruction<br />Spatial reasoning<br />Collaboration</strong><small>PERSISTENT WORLD MODEL</small></div>
    </div>
  );
}

function HardwarePlaceholder() {
  return (
    <div className="bv-placeholder-viz" aria-hidden="true">
      <svg viewBox="0 0 480 320" className="bv-arch-svg">
        <rect x="170" y="70" width="140" height="180" rx="10" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="210" cy="120" r="14" fill="none" stroke="currentColor" />
        <circle cx="270" cy="120" r="14" fill="none" stroke="currentColor" />
        <rect x="190" y="160" width="100" height="18" fill="none" stroke="currentColor" opacity="0.5" />
        <text x="240" y="250" textAnchor="middle" fill="currentColor" fontSize="11" opacity="0.6" fontFamily="IBM Plex Mono, monospace">CAPTURE RIG · PROTOTYPE</text>
      </svg>
    </div>
  );
}
